import * as React from "react"

import { Button } from "@/components/ui/button"
import { Combobox, type ComboboxOption } from "@/components/ui/combobox"
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import type {
  Authority,
  Evidence,
  FindingSeverity,
  PointRect,
} from "../data/review-data"

/**
 * Add-a-finding dialog.
 *
 * Two things about this dialog are load-bearing:
 *
 * 1. AUTHORITY IS NOT A FIELD. A reviewer cannot type a citation. The
 *    authority is derived from the chosen rule, so choosing the rule is
 *    what makes the finding defensible — and that is why the primary
 *    action stays disabled until a rule is picked.
 *
 * 2. THE DRAFTS ARE MARKED. When the pipeline pre-fills text (claim,
 *    reasoning, remediation) from the divergence, each textarea shows
 *    a "Draft — edit before saving" badge. The badge clears on the
 *    first keystroke — so the reader always knows whether a sentence
 *    was written by them or by the machine.
 */

/** Rule catalogue entry. In production this comes from the rule engine. */
export interface RuleOption {
  id: string
  label: string
  /** e.g. "precautionary statements" — filters the picker to the boxed section. */
  section?: string
  /** Default severity for the rule. Overrideable. */
  severity: FindingSeverity
  /** Which KPI group this rolls up to. */
  groupId: "g-text" | "g-format"
  /** The citation attached to the rule. Never editable in this dialog. */
  authorities: Authority[]
  /** Draft copy the pipeline uses to pre-fill the narrative fields. */
  draft?: {
    claim?: string
    reasoning?: string
    remediation?: string
  }
}

/** Optional context set by drawing a box on the label. */
export interface DrawContext {
  bbox: PointRect
  sectionKey?: string
  sectionLabel?: string
}

export interface AddFindingInput {
  ruleId: string
  severity: FindingSeverity
  claim: string
  reasoning: string | null
  remediation: string | null
  authorities: Authority[]
  bbox: PointRect | null
  pageHint: number | null
  groupId: "g-text" | "g-format"
  /** Named page region resolved from the drawn box, if any. */
  sectionId: string | null
  divergence: Evidence | null
  occurrences: number
}

export interface AddFindingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: AddFindingInput) => void
  /** Available rules. Empty is legal — the picker will say so. */
  rules: RuleOption[]
  /** Set by draw mode. Undefined means the reviewer opened the dialog manually. */
  drawContext?: DrawContext | null
  /**
   * Divergence detected by the pipeline for the drawn area, if any. Shown
   * read-only in the "Divergence" pair; the reviewer edits either side
   * only if alignment looks off.
   */
  detectedDivergence?: Evidence | null
}

const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  error: "Error — must be corrected",
  warning: "Warning — should be reviewed",
  info: "Info — worth flagging",
}

/** Human-readable group headings for the rule picker, keyed by section. */
const RULE_SECTION_LABEL: Record<string, string> = {
  front_panel: "Front panel",
  precautionary_statements: "Precautionary statements",
  directions_for_use: "Directions for use",
  storage_disposal: "Storage & disposal",
  ingredients: "Ingredients",
  first_aid: "First aid",
}

export function AddFindingDialog({
  open,
  onOpenChange,
  onSubmit,
  rules,
  drawContext,
  detectedDivergence,
}: AddFindingDialogProps): JSX.Element {
  const [ruleId, setRuleId] = React.useState("")
  const [severity, setSeverity] = React.useState<FindingSeverity>("error")
  const [occurrences, setOccurrences] = React.useState("1")
  const [summary, setSummary] = React.useState("")
  const [summaryDirty, setSummaryDirty] = React.useState(false)
  const [reasoning, setReasoning] = React.useState("")
  const [reasoningDirty, setReasoningDirty] = React.useState(false)
  const [remediation, setRemediation] = React.useState("")
  const [remediationDirty, setRemediationDirty] = React.useState(false)
  const [divergenceText, setDivergenceText] = React.useState("")

  const ruleOptions = React.useMemo<ComboboxOption[]>(
    () =>
      rules.map((r) => ({
        value: r.id,
        label: r.id,
        description: r.label,
        group: r.section ? RULE_SECTION_LABEL[r.section] ?? "General" : "General",
        keywords: r.section ? [r.section, r.id, r.label] : [r.id, r.label],
      })),
    [rules],
  )

  const ruleScope = React.useMemo(() => {
    if (!drawContext?.sectionKey) return null
    const inSection = rules.filter(
      (r) => !r.section || r.section === drawContext.sectionKey,
    )
    if (inSection.length === rules.length) return null
    return {
      label: drawContext.sectionLabel ?? drawContext.sectionKey,
      values: inSection.map((r) => r.id),
    }
  }, [rules, drawContext])

  const selectedRule = React.useMemo(
    () => rules.find((r) => r.id === ruleId) ?? null,
    [rules, ruleId],
  )

  // Rule change primes severity + draft narrative fields the reviewer has
  // not touched yet, so re-picking a rule cannot silently erase edits.
  React.useEffect(() => {
    if (!selectedRule) return
    setSeverity(selectedRule.severity)
    if (!summaryDirty && selectedRule.draft?.claim) setSummary(selectedRule.draft.claim)
    if (!reasoningDirty && selectedRule.draft?.reasoning)
      setReasoning(selectedRule.draft.reasoning)
    if (!remediationDirty && selectedRule.draft?.remediation)
      setRemediation(selectedRule.draft.remediation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRule?.id])

  React.useEffect(() => {
    if (!detectedDivergence) return
    const m = detectedDivergence.master ?? "…"
    const p = detectedDivergence.printed ?? "…"
    setDivergenceText(`Master: ${m} / FPL: ${p}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedDivergence?.master, detectedDivergence?.printed])

  const canSubmit =
    ruleId.trim().length > 0 &&
    summary.trim().length > 0 &&
    Number(occurrences) >= 1

  const reset = () => {
    setRuleId("")
    setSeverity("error")
    setOccurrences("1")
    setSummary("")
    setSummaryDirty(false)
    setReasoning("")
    setReasoningDirty(false)
    setRemediation("")
    setRemediationDirty(false)
    setDivergenceText("")
  }

  const submit = () => {
    if (!canSubmit || !selectedRule) return
    const parsedDivergence = parseDivergence(divergenceText)
    onSubmit({
      ruleId: selectedRule.id,
      severity,
      claim: summary.trim(),
      reasoning: reasoning.trim() || null,
      remediation: remediation.trim() || null,
      authorities: selectedRule.authorities,
      bbox: drawContext?.bbox ?? null,
      pageHint: drawContext?.bbox.page ?? null,
      groupId: selectedRule.groupId,
      sectionId: drawContext?.sectionKey ?? null,
      divergence: parsedDivergence,
      occurrences: Math.max(1, Number(occurrences) || 1),
    })
    reset()
    onOpenChange(false)
  }

  const description = drawContext
    ? `You drew a box on page ${drawContext.bbox.page}. It still cites a rule and its authority — the defensibility standard does not depend on who authored the finding.`
    : "Cites a rule and its authority — the defensibility standard does not depend on who authored the finding."

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent
        size="lg"
        title="Add a finding"
        description={description}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost" size="md">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              size="md"
              onClick={submit}
              aria-disabled={!canSubmit}
              title={
                !canSubmit
                  ? "Pick a rule and write one sentence for the summary."
                  : undefined
              }
              className={cn(
                !canSubmit &&
                  "cursor-not-allowed opacity-60 hover:bg-brand focus-visible:outline-[color:var(--brand)]",
              )}
            >
              Add finding
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Rule" required>
            <Combobox
              options={ruleOptions}
              value={ruleId || null}
              onValueChange={(v) => setRuleId(v ?? "")}
              placeholder="Choose the rule this violates"
              searchPlaceholder="Search by id or wording (e.g. FP-01, signal word)"
              scope={ruleScope}
              mono
              emptyMessage="No matching rules."
            />
          </FormField>

          <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-3">
            <FormField label="Severity">
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as FindingSeverity)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="error">{SEVERITY_LABEL.error}</SelectItem>
                  <SelectItem value="warning">{SEVERITY_LABEL.warning}</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Occurrences on label">
              <Input
                type="number"
                min={1}
                value={occurrences}
                onChange={(e) => setOccurrences(e.currentTarget.value)}
                className="font-id tabular-nums"
              />
            </FormField>
          </div>

          <FormField label="Summary" required>
            <Textarea
              rows={2}
              value={summary}
              onChange={(e) => {
                setSummaryDirty(true)
                setSummary(e.currentTarget.value)
              }}
              placeholder="What is wrong, in one sentence."
            />
          </FormField>

          <FormField label="Divergence — master vs printed">
            <Textarea
              rows={2}
              value={divergenceText}
              onChange={(e) => setDivergenceText(e.currentTarget.value)}
              placeholder="Master: … / FPL: …"
            />
          </FormField>

          <FormField label="Reasoning">
            <Textarea
              rows={3}
              value={reasoning}
              onChange={(e) => {
                setReasoningDirty(true)
                setReasoning(e.currentTarget.value)
              }}
              placeholder="Why this is a violation."
            />
          </FormField>

          <FormField label="Remediation">
            <Textarea
              rows={2}
              value={remediation}
              onChange={(e) => {
                setRemediationDirty(true)
                setRemediation(e.currentTarget.value)
              }}
              placeholder="What the client should change. Never 'edit the master'."
            />
          </FormField>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Split the single divergence field into a `{master, printed}` pair.
 * Accepts either `Master: ... / FPL: ...` or `Master: ... / Printed: ...`
 * separators; blank sides map to null. Free-form text with no separator
 * lands in `printed` because that's what the reviewer typed as-is.
 */
function parseDivergence(text: string): Evidence | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const match = trimmed.match(/master\s*:\s*([\s\S]*?)\s*\/\s*(?:fpl|printed)\s*:\s*([\s\S]*)/i)
  if (!match) return { master: null, printed: trimmed }
  const master = match[1]?.trim() ?? ""
  const printed = match[2]?.trim() ?? ""
  return {
    master: master && master !== "…" ? master : null,
    printed: printed && printed !== "…" ? printed : null,
  }
}
